import { ProposalLayout, UpdateProposalContentSettingsHasManyInput } from "generated/graphql";
import { z } from "zod";

export const sendProposalSchema = z.object({
  proposalId: z.string().min(1, "Proposal ID is required"),
  layout: z.nativeEnum(ProposalLayout),
  proposalNotes: z.string().nullable(),
  emails: z.array(z.string().min(1, "Email is required")),
  removeUnitPriceAndQuantity: z.boolean().optional(),
  removeIndividualLineItemPricing: z.boolean().optional(),
  attachments: z.array(z.string()).optional(),
  acceptanceOverride: z.string().optional().or(z.null()),
  emailSubjectOverride: z.string().optional().or(z.null()),
  emailBodyOverride: z.string().optional().or(z.null()),
  clientEmailOverride: z.string().optional().or(z.null()),
  contentSettingIds: z.array(z.string()).optional(),
});

export type SendProposalFormValues = z.infer<typeof sendProposalSchema>;

type ProposalContentSettingLike = {
  id: string;
  isEnabled?: boolean | null;
};

type ProposalDefaultsLike = {
  id: string;
  layout?: ProposalLayout | string | null;
  proposalNotes?: string | null;
  contact?: { email?: string | null } | null;
  removeUnitPriceAndQuantity?: boolean | null;
  removeIndividualLineItemPricing?: boolean | null;
  attachments?: Array<{ id: string }> | null;
  acceptanceOverride?: string | null;
  emailSubjectOverride?: string | null;
  emailBodyOverride?: string | null;
  clientEmailOverride?: string | null;
  contentSettings?: ProposalContentSettingLike[] | null;
};

export const getDefaultValues = (proposal: ProposalDefaultsLike): SendProposalFormValues => ({
  proposalId: proposal.id,
  layout: (proposal.layout as ProposalLayout) ?? ProposalLayout.SIMPLE,
  proposalNotes: proposal.proposalNotes ?? "",
  emails: [proposal.contact?.email ?? ""],
  removeUnitPriceAndQuantity: !!proposal.removeUnitPriceAndQuantity,
  removeIndividualLineItemPricing: !!proposal.removeIndividualLineItemPricing,
  attachments: proposal.attachments?.map((at) => at.id) ?? [],
  acceptanceOverride: proposal.acceptanceOverride ?? null,
  emailSubjectOverride: proposal.emailSubjectOverride ?? null,
  emailBodyOverride: proposal.emailBodyOverride ?? null,
  clientEmailOverride: proposal.clientEmailOverride ?? null,
  contentSettingIds:
    proposal.contentSettings?.filter((item) => item.isEnabled).map((item) => item.id) ?? [],
});

export const mapContentSettingsInput = (
  contentSettings: ProposalContentSettingLike[] | null | undefined,
  selectedIds?: string[]
): UpdateProposalContentSettingsHasManyInput | undefined => {
  if (!contentSettings?.length) return undefined;
  const selected = selectedIds ?? [];
  return {
    upsert: contentSettings.map((item) => ({
      id: item.id,
      isEnabled: selected.includes(item.id),
    })),
  };
};

export const toProposalMutationVariables = <T extends { contentSettingIds?: string[] }>(
  values: T,
  contentSettings: ProposalContentSettingLike[] | null | undefined
): Omit<T, "contentSettingIds"> & {
  contentSettings?: UpdateProposalContentSettingsHasManyInput;
} => {
  const { contentSettingIds, ...baseValues } = values;
  return {
    ...baseValues,
    contentSettings: mapContentSettingsInput(contentSettings, contentSettingIds),
  };
};
